import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { FirstBookingFunnelPanel } from "./FirstBookingFunnelPanel";
import { listTenants, searchTenants, type TenantSearchHit } from "./opsApi";

/**
 * Cross-tenant tenant search (super-admin.md, DOMAIN.md §C.13). One box matches account/venue names,
 * slugs, owner email, national-format phone (normalized server-side, §B.3) and ids; every hit LIVE-joins
 * the currently-owning account (the post-transfer edge case). A hit links to the tenant record.
 *
 * With an EMPTY box the page browses instead of prompting: it lists every establishment, alphabetical and
 * keyset-paginated ("load more"). Typing a query switches to search; clearing it returns to the list.
 */
export function OpsSearchPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<TenantSearchHit[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [state, setState] = useState<"idle" | "loading" | "loadingMore" | "error">("idle");

  async function loadList(cursor?: string) {
    setState(cursor ? "loadingMore" : "loading");
    try {
      const page = await listTenants(cursor);
      setHits((prev) => (cursor ? [...prev, ...page.items] : page.items));
      setNextCursor(page.nextCursor);
      setSearched(false);
      setState("idle");
    } catch {
      setState("error");
    }
  }

  // Populate the default list on first render so the console never opens on a blank prompt.
  useEffect(() => {
    void loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function run(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) {
      void loadList(); // empty box -> back to the full establishment list
      return;
    }
    setState("loading");
    try {
      setHits(await searchTenants(q));
      setNextCursor(null); // search results are not paginated
      setSearched(true);
      setState("idle");
    } catch {
      setState("error");
    }
  }

  return (
    <section>
      <FirstBookingFunnelPanel />
      <h1 className={css({ fontSize: "xl", fontWeight: "bold", mb: "4" })}>
        {t("ops.search.title")}
      </h1>
      <form onSubmit={run} className={css({ display: "flex", gap: "2", mb: "5" })}>
        <input
          aria-label={t("ops.search.label")}
          placeholder={t("ops.search.placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className={css({
            flex: "1",
            px: "3",
            py: "2",
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
          })}
        />
        <button
          type="submit"
          className={css({
            px: "4",
            py: "2",
            bg: "colorPalette.default",
            color: "colorPalette.fg",
            rounded: "md",
          })}
        >
          {t("ops.search.submit")}
        </button>
      </form>

      {state === "loading" && <p>{t("ops.search.loading")}</p>}
      {state === "error" && <p className={css({ color: "fg.error" })}>{t("ops.search.error")}</p>}
      {state === "idle" && hits.length === 0 && (
        <p className={css({ color: "fg.muted" })}>
          {searched ? t("ops.search.empty") : t("ops.list.empty")}
        </p>
      )}

      <ul className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
        {hits.map((h) => (
          <li key={h.venueId}>
            <Link
              to={`/ops/tenants/${h.tenantId}`}
              className={css({
                display: "flex",
                justifyContent: "space-between",
                p: "3",
                borderWidth: "1px",
                borderColor: "border.default",
                rounded: "md",
                _hover: { bg: "bg.subtle" },
              })}
            >
              <span>
                <strong>{h.venueDisplayName}</strong>{" "}
                <span className={css({ color: "fg.muted" })}>· {h.accountName}</span>
              </span>
              <span className={css({ color: "fg.muted", fontSize: "sm" })}>
                {h.slug} · {h.lifecycle}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      {nextCursor && (
        <button
          type="button"
          onClick={() => void loadList(nextCursor)}
          disabled={state === "loadingMore"}
          className={css({
            mt: "4",
            px: "4",
            py: "2",
            borderWidth: "1px",
            borderColor: "border.default",
            rounded: "md",
            _hover: { bg: "bg.subtle" },
            _disabled: { opacity: "0.6", cursor: "not-allowed" },
          })}
        >
          {state === "loadingMore" ? t("ops.search.loading") : t("ops.list.loadMore")}
        </button>
      )}
    </section>
  );
}
